import React, { useState } from 'react';
import { 
  Trophy, 
  Users, 
  BookOpen, 
  Building2, 
  Code2, 
  Award, 
  Rocket,
  Laptop,
  Target,
  Star,
  GraduationCap,
  Globe,
  HeartHandshake,
  ChevronRight
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface Achievement {
  icon: React.ComponentType<any>;
  title: string;
  description: string;
  detail: string;
  category: 'education' | 'development' | 'recognition' | 'impact';
  stats?: {
    label: string;
    value: string;
  }[];
}

const achievements: Achievement[] = [
  { 
    icon: GraduationCap,
    title: "500+",
    description: "Alunos Impactados",
    detail: "Desenvolvendo a próxima geração de programadores através de mentorias e cursos personalizados",
    category: 'education',
    stats: [
      { label: 'Satisfação', value: '98%' },
      { label: 'Conclusão', value: '90%' }
    ]
  },
  { 
    icon: Code2,
    title: "15+",
    description: "Projetos Entregues",
    detail: "Desenvolvimento de soluções inovadoras e de alto impacto para empresas de diversos setores",
    category: 'development',
    stats: [
      { label: 'No Prazo', value: '95%' },
      { label: 'Qualidade', value: '4.9/5' }
    ]
  },
  { 
    icon: Star,
    title: "5+",
    description: "Reconhecimentos",
    detail: "Prêmios e menções por contribuições significativas para a comunidade tech",
    category: 'recognition',
    stats: [
      { label: 'Eventos', value: '20+' },
      { label: 'Citações', value: '10+' }
    ]
  },
  { 
    icon: HeartHandshake,
    title: "1000+",
    description: "Horas Mentorando",
    detail: "Dedicação ao ensino e desenvolvimento de novos talentos em tecnologia",
    category: 'education',
    stats: [
      { label: 'Mentorias', value: '100+' },
      { label: 'NPS', value: '95%' }
    ]
  }
];

const categoryConfig = {
  education: {
    icon: BookOpen,
    gradient: 'from-blue-500 to-blue-700',
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/20',
    hover: 'hover:border-blue-500/40',
    text: 'text-blue-400'
  },
  development: {
    icon: Code2,
    gradient: 'from-blue-400 to-blue-600',
    bg: 'bg-blue-400/10',
    border: 'border-blue-400/20',
    hover: 'hover:border-blue-400/40',
    text: 'text-blue-400'
  },
  recognition: {
    icon: Trophy,
    gradient: 'from-blue-300 to-blue-500',
    bg: 'bg-blue-300/10',
    border: 'border-blue-300/20',
    hover: 'hover:border-blue-300/40',
    text: 'text-blue-300'
  },
  impact: {
    icon: Target,
    gradient: 'from-blue-600 to-blue-800',
    bg: 'bg-blue-600/10',
    border: 'border-blue-600/20',
    hover: 'hover:border-blue-600/40',
    text: 'text-blue-500'
  }
};

const AchievementCard = ({ icon: Icon, title, description, detail, category, stats }: Achievement) => {
  const config = categoryConfig[category];
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="group"
    >
      <div className={`h-full bg-black/50 border ${config.border} rounded-2xl backdrop-blur-sm transition-all duration-300 ${config.hover} overflow-hidden`}>
        <div className="p-6 space-y-4">
          <div className="flex items-start justify-between">
            <div className={`p-3 rounded-xl ${config.bg}`}>
              <Icon className={`w-6 h-6 ${config.text}`} />
            </div>
            <div className={`text-4xl font-bold ${config.text}`}>
              {title}
            </div>
          </div>

          <div>
            <h3 className="text-xl font-semibold text-white mb-2">
              {description}
            </h3>
            <p className="text-sm text-blue-200 leading-relaxed">
              {detail}
            </p>
          </div>

          {stats && (
            <div className="grid grid-cols-2 gap-3 pt-4">
              {stats.map((stat, index) => (
                <div 
                  key={index}
                  className={`p-3 rounded-xl ${config.bg} text-center`}
                >
                  <div className={`text-lg font-bold ${config.text}`}>
                    {stat.value}
                  </div>
                  <div className="text-xs text-blue-300">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className={`h-1 w-full bg-gradient-to-r ${config.gradient}`} />
      </div>
    </motion.div>
  );
};

const CategoryButton = ({ 
  category, 
  isActive, 
  onClick 
}: { 
  category: { id: string; label: string }; 
  isActive: boolean; 
  onClick: () => void; 
}) => {
  const config = categoryConfig[category.id as keyof typeof categoryConfig] || categoryConfig.development;
  
  return (
    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      className={`
        px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 flex items-center gap-2
        ${isActive 
          ? `bg-gradient-to-r ${config.gradient} text-white shadow-lg shadow-blue-500/20` 
          : 'bg-blue-900/20 text-blue-300 hover:bg-blue-800/30 hover:text-white'}
      `}
    >
      {config.icon && <config.icon className="w-4 h-4" />}
      {category.label}
      {isActive && <ChevronRight className="w-4 h-4" />}
    </motion.button>
  );
};

const Achievements = () => {
  const [filter, setFilter] = useState('all');
  
  const categories = [
    { id: 'all', label: 'Todas' },
    { id: 'education', label: 'Educação' },
    { id: 'development', label: 'Desenvolvimento' },
    { id: 'recognition', label: 'Reconhecimento' },
    { id: 'impact', label: 'Impacto' }
  ];

  const filteredAchievements = achievements.filter(
    achievement => filter === 'all' || achievement.category === filter
  );

  return (
    <section className="relative py-20">
      <div className="absolute inset-0 bg-gradient-to-b from-black via-blue-900/20 to-black" />
      
      <div className="container mx-auto px-4 relative">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl mx-auto text-center mb-16"
        >
          <motion.span 
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/20 text-blue-400 text-sm mb-4"
            whileHover={{ scale: 1.05 }}
          >
            <Trophy className="w-5 h-5" />
            <span>Conquistas & Impacto</span>
          </motion.span>
          
          <h2 className="text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-blue-600 mb-6">
            Resultados Que Fazem a Diferença
          </h2>
          <p className="text-blue-200 text-lg max-w-2xl mx-auto">
            Explore os principais resultados e o impacto do meu trabalho ao longo dos anos, 
            demonstrando meu compromisso com a excelência e a inovação.
          </p>
        </motion.div>

        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {categories.map(category => (
              <CategoryButton
                key={category.id}
                category={category}
                isActive={filter === category.id}
                onClick={() => setFilter(category.id)}
              />
            ))}
          </div>
          
          <motion.div 
            layout
            className="grid sm:grid-cols-1 md:grid-cols-2 gap-8"
          >
            <AnimatePresence mode="wait">
              {filteredAchievements.map((achievement, index) => (
                <AchievementCard key={achievement.description} {...achievement} />
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Achievements;